import React from 'react';
import PropTypes from 'prop-types';
import Janitor from '../modules/Janitor';
import featureFlags from '../modules/FeatureFlags';

export const FlagContext = React.createContext(false);

/**
 * Show or hide parts of a template based on the feature flag (waffle) value.
 *
 * NOTE: Neither the enabled nor disabled state are shown until the feature flag
 * query has returned from the server. This request retrieves all feature flags at
 * once and is cached, so the delay will only be on the first request.
 *
 * @example Show only if a feature flag is enabled
 * ```jsx
 *  <Feature.Enabled flag="my-flag-name">
 *    Show me if I'm enabled
 *  </Feature.Enabled>
 * ```
 *
 * @example Show only if a feature flag is disabled
 * ```jsx
 *  <Feature.Disabled flag="my-flag-name" enabled={false}>
 *    Show me if disabled
 *  </Feature.Disabled>
 * ```
 *
 * @example Advanced usage, both enable & disable
 * ```jsx
 *  <Feature flag="my-flag-name">
 *    <Feature.Enabled>
 *      Show me if I'm enabled
 *    </Feature.Enabled>
 *    <Feature.Disabled>
 *      Show me if disabled
 *    </Feature.Disabled>
 *  </Feature>
 * ```
 */
export default function Feature({ flag, children }) {
  return (
    <FlagContext.Provider value={flag}>
      {children}
    </FlagContext.Provider>
  );
}

Feature.propTypes = {
  /** The name of the feature flag to pass down to Feature.Enabled & Feature.Disabled */
  flag: PropTypes.string.isRequired,

  /** Child elements */
  children: PropTypes.node,
};

Feature.defaultProps = {
  children: undefined,
};

/**
 * Helper function to create the Feature Flag enable/disabled components.
 * @param {Boolean} showIf - Show the child elements if this matches the feature flag value.
 */
function generateFlagComponent(showIf) {
  class FlagComponent extends React.Component {
    static propTypes = {
      /** The name of the feature flag to use.
       *  If no flag is provided, it will look for the flag set by a parent <Feature> component */
      flag: PropTypes.string,

      /** The content to display. */
      children: PropTypes.node,
    };

    static defaultProps = {
      flag: null,
      children: undefined,
    };

    state = {
      flagEnabled: null,
    };

    janitor = new Janitor();

    componentDidMount() {
      this.loadFeatureFlag();
    }

    componentDidUpdate(prevProps) {
      if (prevProps.flag !== this.props.flag) {
        this.loadFeatureFlag();
      }
    }

    componentWillUnmount() {
      this.janitor.cleanup();
    }

    /**
     * Get the feature flag value and set it to the state.
     */
    loadFeatureFlag() {
      const { flag } = this.props;
      if (!flag) {
        return;
      }

      this.janitor.addPromise(featureFlags.isEnabled(flag))
        .then(flagEnabled => {
          this.setState({
            flagEnabled,
          });
        })
        .catch(() => {
          this.setState({
            flagEnabled: false,
          });
        });
    }

    render() {
      const propFlag = this.props.flag;
      const { children } = this.props;
      const { flagEnabled } = this.state;
      const childrenOrDefault = children || null;

      // Flag in props should supersede context flag
      if (propFlag) {
        return (flagEnabled === showIf) ? childrenOrDefault : null;
      }

      // Get the flag name from the context
      return (
        <FlagContext.Consumer>
          {flagName => (
            <FlagComponent flag={flagName}>
              {childrenOrDefault}
            </FlagComponent>
          )}
        </FlagContext.Consumer>
      );
    }
  }
  return FlagComponent;
}

/**
 * If used within a <Feature> component with a name property,
 * it will display the child content if the feature flag is enabled.
 *
 * @example
 * ```jsx
 *  <Feature.Enabled flag="my-flag-name">
 *    Show me if I'm enabled
 *  </Feature.Enabled>
 * ```
 */
Feature.Enabled = generateFlagComponent(true);

/**
 * If used within a <Feature> component with a name property,
 * it will display the child content if the feature flag is disabled.
 *
 * @example
 * ```jsx
 *  <Feature.Disabled flag="my-flag-name">
 *    Show me if disabled
 *  </Feature.Disabled>
 * ```
 */
Feature.Disabled = generateFlagComponent(false);
